/**
 * Shared name normalization and fuzzy matching (Dice bigram similarity).
 * Used by campaign import UIs and pack booking import analyse.
 */

export function normalizeMatchName(input: unknown): string {
  if (typeof input !== "string") return "";
  return input.trim().replace(/\s+/g, " ").toLowerCase();
}

/** Dice bigram similarity (0–1). Case-insensitive, punctuation-normalised. */
export function digramScore(a: string, b: string): number {
  const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, " ").replace(/\s+/g, " ").trim();
  const na = norm(a);
  const nb = norm(b);
  if (!na || !nb) return 0;
  if (na === nb) return 1;
  if (na.includes(nb) || nb.includes(na)) return 0.9;
  const bigrams = (s: string): string[] =>
    Array.from({ length: Math.max(0, s.length - 1) }, (_, i) => s.slice(i, i + 2));
  const ba = bigrams(na);
  const bb = new Set(bigrams(nb));
  const shared = ba.filter((g) => bb.has(g)).length;
  return (2 * shared) / (ba.length + bb.size || 1);
}

export type FuzzyMatchResult<T extends { id: number; name: string }> = T & { score: number };

export function topFuzzyMatchesByName<T extends { id: number; name: string }>(
  importName: string,
  candidates: T[],
  threshold = 0.35,
): FuzzyMatchResult<T>[] {
  const scored = candidates
    .map((candidate) => ({ ...candidate, score: digramScore(importName, candidate.name) }))
    .filter((candidate) => candidate.score >= threshold)
    .sort((a, b) => b.score - a.score);

  if (scored.length === 0) return [];

  const topScore = scored[0].score;
  return scored.filter((candidate) => Math.abs(candidate.score - topScore) < 0.0001);
}

export function bestFuzzyMatchByName<T extends { id: number; name: string }>(
  importName: string,
  candidates: T[],
  threshold = 0.35,
): FuzzyMatchResult<T> | null {
  const top = topFuzzyMatchesByName(importName, candidates, threshold);
  return top.length === 1 ? top[0] : null;
}
